/**
 * Éclat Promo Popup — behavior + tracking (customeclat).
 *
 * Works with sections/eclat-promo-popup.liquid, which renders the theme's
 * native <pop-in> element. This script adds:
 *  - frequency capping (show once per N days, localStorage per handle)
 *  - delayed automatic opening (skipped in the theme editor — there the
 *    popup opens via the native handle-editor-events behavior)
 *  - event tracking through the Éclat A/B pipeline (window.EclatAB):
 *      impression  – popup shown
 *      click       – CTA clicked (destination handoff → dest_view fires
 *                    on the target page via eclat-ab.js)
 *      signup      – e-mail subscribed to the Klaviyo list (Client API)
 *      add_to_cart / orders — attributed automatically via markExposed
 *
 * Events appear in the dashboard under the popup's tracking handle as
 * variant A (split 0 = tracked element, not an A/B split).
 */
(function () {
  'use strict';

  if (window.__eclatPopupInit) return;
  window.__eclatPopupInit = true;

  var DESIGN = window.Shopify && window.Shopify.designMode;

  function sessionFlag(key) {
    try { return sessionStorage.getItem('_eab_' + key) === '1'; } catch (e) { return false; }
  }
  function setSessionFlag(key) {
    try { sessionStorage.setItem('_eab_' + key, '1'); } catch (e) {}
  }

  function initPopup(el) {
    if (el.__eabBound) return;
    el.__eabBound = true;

    var AB = window.EclatAB;
    var handle = el.getAttribute('data-eab-popup') || 'promo-popup';
    var delayMs = (parseInt(el.getAttribute('data-delay'), 10) || 0) * 1000;
    var freqDays = parseInt(el.getAttribute('data-frequency-days'), 10);
    if (isNaN(freqDays)) freqDays = 5;
    var capKey = '_eab_pop_' + handle;

    function canTrack() {
      return AB && !AB.bot && AB.vid && !DESIGN;
    }

    function markShown() {
      try { localStorage.setItem(capKey, String(Date.now())); } catch (e) {}
    }

    var signupHideDays = parseInt(el.getAttribute('data-signup-hide-days'), 10);
    if (isNaN(signupHideDays)) signupHideDays = 365;
    var doneKey = '_eab_pop_done_' + handle;

    function frequencyAllows() {
      try {
        // a visitor who already subscribed through this popup
        var done = parseInt(localStorage.getItem(doneKey) || '0', 10);
        if (done && signupHideDays > 0 && Date.now() - done < signupHideDays * 86400000) return false;
      } catch (e) {}
      if (freqDays <= 0) return true;
      try {
        var last = parseInt(localStorage.getItem(capKey) || '0', 10);
        return !last || Date.now() - last > freqDays * 86400000;
      } catch (e) { return true; }
    }

    function trackImpression() {
      if (!canTrack() || sessionFlag('pimp_' + handle)) return;
      setSessionFlag('pimp_' + handle);
      // attribution map → add_to_cart / orders credit this handle
      AB.markExposed(handle, 'A', 0);
      if (AB.track) AB.track('impression', handle, 'A', {});
    }

    // CTA / any link inside the popup
    el.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (canTrack()) {
        if (!sessionFlag('pclk_' + handle)) {
          setSessionFlag('pclk_' + handle);
          if (AB.track) AB.track('click', handle, 'A', { href: href || '' });
        }
        try {
          var dest = new URL(href, location.origin);
          if (dest.origin === location.origin) {
            localStorage.setItem('_eab_nav', JSON.stringify({
              h: handle, v: 'A', dest: dest.pathname, t: Date.now()
            }));
          }
        } catch (err) {}
        if (AB.flush) AB.flush();
      }
      markShown();
      if (typeof el.hide === 'function') el.hide();
    }, true);

    // newsletter signup → Klaviyo Client API (public key, no page reload)
    var form = el.querySelector('form[data-eab-signup]');
    if (form && !form.__eabBound) {
      form.__eabBound = true;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = form.querySelector('input[type="email"]');
        var hp = form.querySelector('input[name="website"]');
        var msg = form.querySelector('.eclat-promo-popup__signup-msg');
        var fields = form.querySelector('.eclat-promo-popup__signup-fields');
        var btn = form.querySelector('button[type="submit"]');
        var email = ((input && input.value) || '').trim();
        var company = form.getAttribute('data-company-id') || '';
        var list = form.getAttribute('data-list-id') || '';

        function show(text, ok) {
          if (!msg) return;
          msg.textContent = text || '';
          msg.classList.toggle('is-success', ok);
          msg.classList.toggle('is-error', !ok);
          msg.hidden = false;
        }
        function succeed() {
          if (fields) fields.hidden = true;
          show(form.getAttribute('data-success'), true);
          markShown();
          try { localStorage.setItem(doneKey, String(Date.now())); } catch (err) {}
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          show(form.getAttribute('data-error'), false);
          if (input) input.focus();
          return;
        }
        // honeypot filled → pretend success, send nothing
        if (hp && hp.value) { succeed(); return; }
        if (!company || !list) { show(form.getAttribute('data-error'), false); return; }

        if (btn) btn.disabled = true;
        var payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: form.getAttribute('data-source') || 'Promo popup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        };
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2025-07-15' },
          body: JSON.stringify(payload)
        }).then(function (r) {
          if (!r.ok) throw new Error('klaviyo ' + r.status);
          succeed();
          // let Klaviyo onsite tracking know who this is (optional, best effort)
          try { if (window.klaviyo && window.klaviyo.push) window.klaviyo.push(['identify', { $email: email }]); } catch (err) {}
          if (canTrack() && !sessionFlag('psgn_' + handle)) {
            setSessionFlag('psgn_' + handle);
            AB.markExposed(handle, 'A', 0);
            if (AB.track) AB.track('signup', handle, 'A', { list: list });
            if (AB.flush) AB.flush();
          }
        }).catch(function () {
          if (btn) btn.disabled = false;
          show(form.getAttribute('data-error'), false);
        });
      });
    }

    // Klaviyo embedded form inside the popup: Klaviyo renders it and fires a
    // window event on submit — we only track and apply the post-signup cap
    var kform = el.querySelector('[data-eab-klaviyo-form]');
    if (kform && !window.__eabKlaviyoFormsBound) {
      window.__eabKlaviyoFormsBound = true;
      window.addEventListener('klaviyoForms', function (e) {
        var d = e && e.detail;
        if (!d || d.type !== 'submit') return;
        var host = document.querySelector('[data-eab-klaviyo-form="' + d.formId + '"]');
        var pop = host && host.closest('pop-in[data-eab-popup]');
        if (!pop) return;
        var h = pop.getAttribute('data-eab-popup') || 'promo-popup';
        try { localStorage.setItem('_eab_pop_' + h, String(Date.now())); } catch (err) {}
        try { localStorage.setItem('_eab_pop_done_' + h, String(Date.now())); } catch (err) {}
        if (canTrack() && !sessionFlag('psgn_' + h)) {
          setSessionFlag('psgn_' + h);
          AB.markExposed(h, 'A', 0);
          if (AB.track) AB.track('signup', h, 'A', { form: String(d.formId || '') });
          if (AB.flush) AB.flush();
        }
      });
    }

    // close when clicking anywhere outside the popup (optional)
    if (el.hasAttribute('data-close-outside') && !DESIGN) {
      document.addEventListener('pointerdown', function (e) {
        if (!el.hasAttribute('open')) return;
        if (el.contains(e.target)) return;
        if (typeof el.hide === 'function') el.hide();
      });
    }

    // optional backdrop (dim layer behind the popup)
    var backdrop = document.querySelector('[data-eab-popup-backdrop="' + el.id + '"]');
    if (backdrop && !backdrop.__eabBound) {
      backdrop.__eabBound = true;
      backdrop.addEventListener('click', function () {
        if (typeof el.hide === 'function') el.hide();
      });
    }
    function syncBackdrop(open) {
      if (!backdrop) return;
      if (open) {
        backdrop.hidden = false;
        requestAnimationFrame(function () { backdrop.classList.add('is-visible'); });
      } else {
        backdrop.classList.remove('is-visible');
        setTimeout(function () { backdrop.hidden = true; }, 300);
      }
    }

    // track when the popup actually opens (also covers editor-triggered opens
    // on the storefront preview and manual el.show() calls)
    new MutationObserver(function () {
      var open = el.hasAttribute('open');
      syncBackdrop(open);
      if (open && !DESIGN) {
        markShown();
        trackImpression();
      }
    }).observe(el, { attributes: true, attributeFilter: ['open'] });

    // automatic opening (storefront only — the editor uses native
    // handle-editor-events to open the popup when the section is selected)
    if (DESIGN) return;
    if (!frequencyAllows()) return;

    setTimeout(function () {
      if (typeof el.show === 'function') el.show();
    }, delayMs);
  }

  function scan() {
    document.querySelectorAll('pop-in[data-eab-popup]').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
